import { Component } from '@theme/component';

/**
 * <versel-discover-styles>: collection tabs with a swipeable product row per tab.
 * Markup comes from sections/versel-discover-styles.liquid and blocks/_versel-discover-tab.liquid.
 */
class VerselDiscoverStyles extends Component {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // scroll doesn't bubble; capture it from every track in this section.
    this.addEventListener('scroll', () => this.#queueUpdate(), { capture: true, passive: true, signal });
    document.addEventListener('shopify:block:select', (event) => this.#onBlockSelect(event), { signal });

    this.#resizeObserver = new ResizeObserver(() => this.#queueUpdate());
    this.#resizeObserver.observe(this);

    this.#init();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  get #tabs() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('button[data-vd-tab]')]);
  }

  #init() {
    const tabs = this.#tabs;
    const current = tabs.find((tab) => tab.getAttribute('aria-expanded') === 'true') ?? tabs[0];
    if (current?.dataset.vdTab) this.#activate(current.dataset.vdTab);
  }

  /**
   * Shows the panel of the clicked tab.
   * @param {Event} event
   */
  selectTab(event) {
    const tab = event.target instanceof Element ? event.target.closest('button[data-vd-tab]') : null;
    if (!(tab instanceof HTMLButtonElement) || !tab.dataset.vdTab) return;
    this.#activate(tab.dataset.vdTab);
  }

  /** @param {Event} event */
  scrollPrev(event) {
    this.#scrollFrom(event, -1);
  }

  /** @param {Event} event */
  scrollNext(event) {
    this.#scrollFrom(event, 1);
  }

  /** @param {Event} event */
  goToPage(event) {
    const dot = event.target instanceof Element ? event.target.closest('[data-vd-page]') : null;
    const track = this.#activeTrack();
    if (!(dot instanceof HTMLElement) || !track) return;
    const direction = this.#isRtl() ? -1 : 1;
    track.scrollTo({ left: Number(dot.dataset.vdPage) * track.clientWidth * direction, behavior: this.#behavior() });
  }

  /** @param {string} id */
  #activate(id) {
    for (const tab of this.#tabs) {
      tab.setAttribute('aria-expanded', String(tab.dataset.vdTab === id));
    }
    for (const panel of this.querySelectorAll('[data-vd-panel]')) {
      if (panel instanceof HTMLElement) panel.hidden = panel.dataset.vdPanel !== id;
    }
    this.#queueUpdate();
  }

  /**
   * @param {Event} event
   * @param {1 | -1} step
   */
  #scrollFrom(event, step) {
    const panel = event.target instanceof Element ? event.target.closest('[data-vd-panel]') : null;
    const track = panel?.querySelector('[data-vd-track]');
    if (!(track instanceof HTMLElement)) return;
    const direction = this.#isRtl() ? -1 : 1;
    track.scrollBy({ left: track.clientWidth * step * direction, behavior: this.#behavior() });
  }

  /** @param {Event} event */
  #onBlockSelect(event) {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;
    const tab = target.closest('[data-vd-item]')?.querySelector('button[data-vd-tab]');
    if (tab instanceof HTMLButtonElement && tab.dataset.vdTab) this.#activate(tab.dataset.vdTab);
  }

  #activeTrack() {
    const track = this.querySelector('[data-vd-panel]:not([hidden]) [data-vd-track]');
    return track instanceof HTMLElement ? track : null;
  }

  #queueUpdate() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#updateControls());
  }

  /** Syncs the arrows and dots of the visible panel with its scroll position. */
  #updateControls() {
    const panel = this.querySelector('[data-vd-panel]:not([hidden])');
    const track = this.#activeTrack();
    if (!panel || !track || track.clientWidth === 0) return;

    const offset = Math.abs(track.scrollLeft);
    const maxOffset = track.scrollWidth - track.clientWidth;
    const pages = maxOffset > 2 ? Math.ceil(track.scrollWidth / track.clientWidth - 0.05) : 0;
    const page = offset >= maxOffset - 2 ? pages - 1 : Math.round(offset / track.clientWidth);

    const prev = panel.querySelector('.vd-arrow--prev');
    const next = panel.querySelector('.vd-arrow--next');
    if (prev instanceof HTMLButtonElement) prev.disabled = offset <= 2;
    if (next instanceof HTMLButtonElement) next.disabled = offset >= maxOffset - 2;

    const dots = panel.querySelector('[data-vd-dots]');
    if (!(dots instanceof HTMLElement)) return;
    if (dots.childElementCount !== pages) {
      dots.replaceChildren(
        ...Array.from({ length: pages }, (_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'vd-dot';
          dot.tabIndex = -1;
          dot.dataset.vdPage = String(index);
          dot.setAttribute('on:click', '/goToPage');
          return dot;
        })
      );
    }
    [...dots.children].forEach((dot, index) => dot.setAttribute('aria-current', String(index === page)));
  }

  #isRtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  #behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }
}

if (!customElements.get('versel-discover-styles')) {
  customElements.define('versel-discover-styles', VerselDiscoverStyles);
}
